import { useEffect } from 'react';
import {
  HashRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';

import { BottomTabBar } from '@/components/layout/bottom-tab-bar';
import { useAuth } from '@/hooks/use-auth';
import { hideBackButton, showBackButton } from '@/lib/telegram';

import HomePage from '@/pages/home';
import AssignmentsPage from '@/pages/assignments';
import HistoryPage from '@/pages/history';
import ProfilePage from '@/pages/profile';
import StatsPage from '@/pages/stats';
import LearningHubPage from '@/pages/learning-hub';
import ContentViewerPage from '@/pages/content-viewer';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

const tabRoots = new Set([
  '/',
  '/assignments',
  '/learn',
  '/history',
  '/stats',
  '/profile',
]);

function BackButtonBinding() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (tabRoots.has(location.pathname)) {
      hideBackButton();
      return;
    }
    return showBackButton(() => navigate(-1));
  }, [location.pathname, navigate]);
  return null;
}

function Splash({ error }: { error?: string }) {
  return (
    <div className="flex min-h-[var(--vh)] flex-col items-center justify-center gap-3 px-6 text-center">
      <div className="text-4xl">🌱</div>
      {error ? (
        <>
          <p className="text-sm text-[var(--color-destructive)]">
            Couldn't authorize with Telegram.
          </p>
          <p className="text-xs text-[var(--color-muted)]">{error}</p>
        </>
      ) : (
        <p className="text-sm text-[var(--color-muted)]">Loading your garden…</p>
      )}
    </div>
  );
}

function AppShell() {
  const auth = useAuth();

  if (auth.status === 'pending') return <Splash />;
  if (auth.status === 'error') return <Splash error={auth.error} />;

  return (
    <>
      <BackButtonBinding />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/assignments" element={<AssignmentsPage />} />
        <Route path="/learn" element={<LearningHubPage />} />
        <Route path="/learn/:id" element={<ContentViewerPage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/stats" element={<StatsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <BottomTabBar />
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HashRouter>
        <AppShell />
      </HashRouter>
      <Toaster position="top-center" richColors />
    </QueryClientProvider>
  );
}
